import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import api from '../../utils/api';
import './ModuleCards.css';

// Map builtin_key to internal route
const BUILTIN_ROUTES = {
  time_off: '/time-off',
  tools: '/tools',
};

/**
 * Dynamically resolve a Lucide icon by name string.
 * Falls back to a generic square icon if not found.
 */
function getIcon(iconName) {
  if (!iconName) return LucideIcons.LayoutGrid;
  // Lucide exports use PascalCase; try exact match first
  if (LucideIcons[iconName]) return LucideIcons[iconName];
  // Try common variations
  const pascalCase = iconName
    .replace(/(^|[-_ ])(\w)/g, (_, _sep, c) => c.toUpperCase());
  if (LucideIcons[pascalCase]) return LucideIcons[pascalCase];
  return LucideIcons.LayoutGrid;
}

function ModuleCards() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchModules() {
      try {
        const data = await api.get('/modules');
        setModules(data.modules || []);
      } catch (err) {
        // Silent fail -- modules are non-critical
      } finally {
        setLoading(false);
      }
    }
    fetchModules();
  }, []);

  if (loading) {
    return null; // Don't show skeleton for module cards
  }

  if (modules.length === 0) {
    return null;
  }

  const handleClick = (mod) => {
    if (mod.module_type === 'builtin' && mod.builtin_key) {
      const route = BUILTIN_ROUTES[mod.builtin_key];
      if (route) {
        navigate(route);
      }
    } else if (mod.module_type === 'external' && mod.url) {
      window.open(mod.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="module-cards">
      <h3 className="module-cards-title">Quick Access</h3>
      <div className="module-cards-grid">
        {modules.map((mod) => {
          const Icon = getIcon(mod.icon);
          const isExternal = mod.module_type === 'external';

          return (
            <button
              key={mod.id}
              className="module-card"
              onClick={() => handleClick(mod)}
              type="button"
            >
              <div className="module-card-icon">
                <Icon size={20} strokeWidth={2} />
              </div>
              <div className="module-card-content">
                <span className="module-card-name">
                  {mod.name}
                  {isExternal && <ExternalLink size={12} className="module-card-external" />}
                </span>
                {mod.description && (
                  <span className="module-card-desc">{mod.description}</span>
                )}
              </div>
              <ArrowRight size={14} className="module-card-arrow" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ModuleCards;
